import { useState, useMemo } from 'react';
import { ArrowLeft, Download, BarChart, Table, Filter, AlertTriangle, TrendingUp, Clock, MapPin } from 'lucide-react';

interface StudentHallPassHistoryProps {
  studentId: string;
  onBack: () => void;
  hallPassHistory: any[];
  students: any[];
  teachers: any[];
  hallPassTypes: any[];
}

export default function StudentHallPassHistory({ 
  studentId, 
  onBack, 
  hallPassHistory, 
  students, 
  teachers, 
  hallPassTypes 
}: StudentHallPassHistoryProps) {
  const [viewMode, setViewMode] = useState<'data' | 'analytics'>('data');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState<'all' | 'today' | 'week' | 'month'>('all');

  // Find the student
  const student = students.find(s => s.userId === studentId);

  // Filter hall pass history for this student
  const studentHistory = useMemo(() => {
    const filtered = hallPassHistory.filter(pass => pass.studentId === studentId);
    
    // Apply date filter
    if (dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateRange) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
      }
      
      return filtered.filter(pass => {
        const passDate = new Date(pass.startTime || pass.createdAt);
        return passDate >= filterDate;
      });
    }
    
    return filtered;
  }, [hallPassHistory, studentId, dateRange]);

  // Calculate analytics
  const analytics = useMemo(() => {
    const totalPasses = studentHistory.length;
    const todayPasses = studentHistory.filter(pass => {
      const passDate = new Date(pass.startTime || pass.createdAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return passDate >= today;
    }).length;

    const thisWeekPasses = studentHistory.filter(pass => {
      const passDate = new Date(pass.startTime || pass.createdAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return passDate >= weekAgo;
    }).length;

    // Calculate most used destination
    const destinationCounts = studentHistory.reduce((acc, pass) => {
      const type = hallPassTypes.find(t => t.hallPassTypeId === pass.hallPassTypeId);
      const destination = type?.destinationLocationDetails?.name || 
                         type?.hallPassTypeName || 
                         pass.hallPassTypeDescription || 
                         'Unknown';
      acc[destination] = (acc[destination] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostUsedDestination = Object.entries(destinationCounts)
      .sort(([,a], [,b]) => (b as number) - (a as number))[0];

    // Calculate average duration
    const durations = studentHistory
      .filter(pass => pass.endTime && pass.startTime)
      .map(pass => {
        const start = new Date(pass.startTime);
        const end = new Date(pass.endTime);
        return (end.getTime() - start.getTime()) / (1000 * 60); // minutes
      });
    
    const averageDuration = durations.length > 0 ? 
      durations.reduce((a, b) => a + b, 0) / durations.length : 0;

    // Calculate compliance score (on-time returns)
    const completedPasses = studentHistory.filter(pass => pass.endTime);
    const onTimePasses = completedPasses.filter(pass => {
      if (!pass.startTime || !pass.endTime) return true;
      const start = new Date(pass.startTime);
      const end = new Date(pass.endTime);
      const duration = (end.getTime() - start.getTime()) / (1000 * 60);
      return duration <= (pass.durationMinutes || 10); // Default 10 min if no duration set
    });
    
    const complianceScore = completedPasses.length > 0 ? 
      Math.round((onTimePasses.length / completedPasses.length) * 100) : 100;

    // Detect concerning patterns
    const concerningPatterns = [];
    
    // Pattern 1: Excessive usage (more than 3 passes today)
    if (todayPasses > 3) {
      concerningPatterns.push(`Excessive daily usage (${todayPasses} passes today)`);
    }
    
    // Pattern 2: High frequency this week
    if (thisWeekPasses > 15) {
      concerningPatterns.push(`High weekly usage (${thisWeekPasses} passes this week)`);
    }
    
    // Pattern 3: Low compliance
    if (complianceScore < 70 && completedPasses.length > 5) {
      concerningPatterns.push(`Low compliance score (${complianceScore}% on-time returns)`);
    }

    // Pattern 4: Same destination repeatedly
    if (mostUsedDestination && (mostUsedDestination[1] as number) > totalPasses * 0.7 && totalPasses > 5) {
      concerningPatterns.push(`Repetitive destination usage (${mostUsedDestination[1]} visits to ${mostUsedDestination[0]})`);
    }

    // Calculate risk level
    let riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' = 'LOW';
    if (concerningPatterns.length >= 3 || todayPasses > 5) {
      riskLevel = 'HIGH';
    } else if (concerningPatterns.length >= 1 || thisWeekPasses > 10) {
      riskLevel = 'MEDIUM';
    }

    return {
      totalPasses,
      todayPasses,
      thisWeekPasses,
      mostUsedDestination: mostUsedDestination ? {
        name: mostUsedDestination[0] as string,
        count: mostUsedDestination[1] as number
      } : null,
      averageDuration: Math.round(averageDuration),
      complianceScore,
      concerningPatterns,
      riskLevel
    };
  }, [studentHistory, hallPassTypes]);

  // Export to CSV function
  const exportToCSV = () => {
    const headers = ['Date & Time', 'Type', 'Location', 'Duration', 'Actual', 'Status', 'Teacher', 'Notes'];
    
    const csvData = studentHistory.map(pass => {
      const startDt = new Date(pass.startTime || pass.createdAt);
      const endDt = pass.endTime ? new Date(pass.endTime) : null;
      const type = hallPassTypes.find(t => t.hallPassTypeId === pass.hallPassTypeId);
      const teacher = teachers.find(t => t.userId === pass.teacherId || t.id === pass.teacherId);
      
      const location = type?.destinationLocationDetails?.name || 
                      type?.hallPassTypeName || 
                      pass.hallPassTypeDescription || 
                      'Unknown';
      
      const duration = pass.durationMinutes ? `${pass.durationMinutes}m` : '-';
      const actual = endDt && pass.startTime ? 
        `${Math.round((endDt.getTime() - startDt.getTime()) / (1000 * 60))}m` : '-';
      
      const status = pass.endTime ? 'Returned' : 
                    pass.hallPassStatusId === 1 ? 'Active' : 'Pending';

      return [
        startDt.toLocaleString(),
        type?.hallPassTypeName || 'Unknown',
        location,
        duration,
        actual,
        status,
        teacher?.name || 'Unknown',
        pass.notes || '-'
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${student?.name || 'student'}_hall_pass_history.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!student) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="flex items-center gap-2 mb-4 text-gray-600 hover:text-gray-800">
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="text-center py-12">
          <p className="text-gray-500">Student not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button onClick={onBack} className="flex items-center gap-2 mb-4 text-gray-600 hover:text-gray-800">
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src={student.avatar || ''} 
              alt={student.name} 
              className="w-16 h-16 rounded-full bg-gray-200"
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{student.name}</h1>
              <p className="text-gray-600">Hall Pass History</p>
              <p className="text-sm text-gray-500">Grade {student.grade}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('analytics')}
                className={`px-3 py-2 text-sm font-medium rounded-md flex items-center gap-2 ${
                  viewMode === 'analytics' 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <BarChart className="w-4 h-4" />
                Analytics
              </button>
              <button
                onClick={() => setViewMode('data')}
                className={`px-3 py-2 text-sm font-medium rounded-md flex items-center gap-2 ${
                  viewMode === 'data' 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Table className="w-4 h-4" />
                Data Only
              </button>
            </div>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'analytics' ? (
        /* Analytics View */
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-blue-50 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">{analytics.totalPasses}</div>
              <div className="text-sm font-medium text-gray-800">Total Passes</div>
              <div className="text-xs text-gray-500">
                {analytics.todayPasses} today, {analytics.thisWeekPasses} this week
              </div>
              {analytics.totalPasses > 0 && (
                <div className="mt-2">
                  <TrendingUp className="w-4 h-4 text-blue-500 mx-auto" />
                </div>
              )}
            </div>

            <div className={`rounded-xl p-6 text-center ${
              analytics.riskLevel === 'LOW' ? 'bg-green-50' :
              analytics.riskLevel === 'MEDIUM' ? 'bg-yellow-50' : 'bg-red-50'
            }`}>
              <div className={`text-3xl font-bold mb-2 ${
                analytics.riskLevel === 'LOW' ? 'text-green-600' :
                analytics.riskLevel === 'MEDIUM' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {analytics.riskLevel}
              </div>
              <div className="text-sm font-medium text-gray-800">Risk Level</div>
              <div className="text-xs text-gray-500">
                {analytics.concerningPatterns.length} patterns detected
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">{analytics.complianceScore}/100</div>
              <div className="text-sm font-medium text-gray-800">Compliance Score</div>
              <div className="text-xs text-gray-500">On time returns</div>
            </div>

            <div className="bg-orange-50 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">
                {analytics.mostUsedDestination?.name.split(' ')[0] || '-'}
              </div>
              <div className="text-sm font-medium text-gray-800">Most Used Path</div>
              <div className="text-xs text-gray-500">
                {analytics.mostUsedDestination ? 
                  `${analytics.mostUsedDestination.count} passes` : 
                  'No data'
                }
              </div>
              <MapPin className="w-4 h-4 text-orange-500 mx-auto mt-2" />
            </div>
          </div>

          {/* Usage Patterns by Time of Day */}
          {studentHistory.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Usage Patterns by Time of Day</h3>
              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Morning', range: '7-9 AM', time: [7, 9], color: 'bg-blue-400' },
                  { label: 'Mid-Morning', range: '9 AM-12 PM', time: [9, 12], color: 'bg-green-400' },
                  { label: 'Lunch', range: '12-2 PM', time: [12, 14], color: 'bg-yellow-400' },
                  { label: 'Afternoon', range: '2-4 PM', time: [14, 16], color: 'bg-purple-400' }
                ].map((period) => {
                  const count = studentHistory.filter(pass => {
                    const hour = new Date(pass.startTime || pass.createdAt).getHours();
                    return hour >= period.time[0] && hour < period.time[1];
                  }).length;
                  const maxCount = Math.max(...[7, 9, 12, 14, 16].map((_, i) => 
                    studentHistory.filter(pass => {
                      const hour = new Date(pass.startTime || pass.createdAt).getHours();
                      const ranges = [[7, 9], [9, 12], [12, 14], [14, 16]];
                      return hour >= ranges[i][0] && hour < ranges[i][1];
                    }).length
                  ));
                  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  
                  return (
                    <div key={period.label} className="text-center">
                      <div className="mb-3">
                        <div className={`h-20 ${period.color} rounded-lg mb-2 relative`}>
                          <div 
                            className="absolute bottom-0 w-full bg-gray-700 rounded-lg transition-all duration-300"
                            style={{ height: `${percentage}%` }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center text-white font-bold">
                            {count}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-800">{period.label}</div>
                      <div className="text-xs text-gray-500">{period.range}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Concerning Patterns */}
          {analytics.concerningPatterns.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <h3 className="text-lg font-semibold text-red-800">Concerning Patterns Detected</h3>
              </div>
              <ul className="space-y-2">
                {analytics.concerningPatterns.map((pattern, index) => (
                  <li key={index} className="flex items-start gap-2 text-red-700">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                    {pattern}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Route Pattern Analysis */}
          {analytics.mostUsedDestination && (
            <div className="bg-yellow-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Route Pattern Analysis</h3>
              <div className="text-yellow-800">
                <p className="mb-2">High frequency usage detected on the following routes:</p>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full" />
                  <span className="font-medium">{analytics.mostUsedDestination.name}</span>
                  <span className="text-sm">({analytics.mostUsedDestination.count} visits)</span>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
                >
                  <Filter className="w-4 h-4" />
                  Show Filters
                </button>
              </div>
              
              {showFilters && (
                <div className="mt-4 flex gap-4">
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left p-4 text-sm font-medium text-gray-600">DATE & TIME</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">TYPE</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">LOCATION</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">DURATION</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">ACTUAL</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">STATUS</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">TEACHER</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">NOTES</th>
                  </tr>
                </thead>
                <tbody>
                  {studentHistory.map((pass, index) => {
                    const startDt = new Date(pass.startTime || pass.createdAt);
                    const endDt = pass.endTime ? new Date(pass.endTime) : null;
                    const type = hallPassTypes.find(t => t.hallPassTypeId === pass.hallPassTypeId);
                    const teacher = teachers.find(t => t.userId === pass.teacherId || t.id === pass.teacherId);
                    
                    const location = type?.destinationLocationDetails?.name || 
                                    type?.hallPassTypeName || 
                                    pass.hallPassTypeDescription || 
                                    'Unknown';
                    
                    const duration = pass.durationMinutes ? `${pass.durationMinutes}m` : '-';
                    const actual = endDt && pass.startTime ? 
                      `${Math.round((endDt.getTime() - startDt.getTime()) / (1000 * 60))}m` : '-';
                    
                    const status = pass.endTime ? 'Returned' : 
                                  pass.hallPassStatusId === 1 ? 'Active' : 'Pending';

                    return (
                      <tr key={pass.hallPassId || index} className="border-b border-gray-50">
                        <td className="p-4">
                          <div className="text-sm text-gray-900">
                            {startDt.toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {startDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {type?.hallPassTypeName || 'Unknown'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-900">{location}</td>
                        <td className="p-4 text-sm text-gray-900">{duration}</td>
                        <td className="p-4 text-sm text-gray-900">{actual}</td>
                        <td className="p-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            status === 'Returned' ? 'bg-green-100 text-green-800' :
                            status === 'Active' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-900">{teacher?.name || 'Unknown'}</td>
                        <td className="p-4 text-sm text-gray-500">{pass.notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* Data Only View */
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">{analytics.totalPasses}</div>
              <div className="text-sm font-medium text-gray-800">Total Passes</div>
              <div className="text-xs text-gray-500">
                {analytics.todayPasses} today, {analytics.thisWeekPasses} this week
              </div>
            </div>

            <div className={`rounded-xl p-6 text-center ${
              analytics.riskLevel === 'LOW' ? 'bg-green-50' :
              analytics.riskLevel === 'MEDIUM' ? 'bg-yellow-50' : 'bg-red-50'
            }`}>
              <div className={`text-3xl font-bold mb-2 ${
                analytics.riskLevel === 'LOW' ? 'text-green-600' :
                analytics.riskLevel === 'MEDIUM' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {analytics.riskLevel}
              </div>
              <div className="text-sm font-medium text-gray-800">Risk Level</div>
            </div>

            <div className="bg-purple-50 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">{analytics.complianceScore}/100</div>
              <div className="text-sm font-medium text-gray-800">Compliance Score</div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
              >
                <Filter className="w-4 h-4" />
                Show Filters
              </button>
            </div>
            
            {showFilters && (
              <div className="flex gap-4">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </select>
              </div>
            )}
          </div>

          {/* Data Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left p-4 text-sm font-medium text-gray-600">DATE & TIME</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">TYPE</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">LOCATION</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">DURATION</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">ACTUAL</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">STATUS</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">TEACHER</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-600">NOTES</th>
                  </tr>
                </thead>
                <tbody>
                  {studentHistory.map((pass, index) => {
                    const startDt = new Date(pass.startTime || pass.createdAt);
                    const endDt = pass.endTime ? new Date(pass.endTime) : null;
                    const type = hallPassTypes.find(t => t.hallPassTypeId === pass.hallPassTypeId);
                    const teacher = teachers.find(t => t.userId === pass.teacherId || t.id === pass.teacherId);
                    
                    const location = type?.destinationLocationDetails?.name || 
                                    type?.hallPassTypeName || 
                                    pass.hallPassTypeDescription || 
                                    'Unknown';
                    
                    const duration = pass.duration ? `${pass.duration}m` : '-';
                    const actual = endDt && pass.startTime ? 
                      `${Math.round((endDt.getTime() - startDt.getTime()) / (1000 * 60))}m` : '-';
                    
                    const status = pass.endTime ? 'Returned' : 
                                  pass.hallPassStatusId === 1 ? 'Active' : 'Pending';

                    return (
                      <tr key={pass.hallPassId || index} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="p-4">
                          <div className="text-sm text-gray-900">
                            {startDt.toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {startDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {type?.hallPassTypeName || 'Unknown'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-900">{location}</td>
                        <td className="p-4 text-sm text-gray-900">{duration}</td>
                        <td className="p-4 text-sm text-gray-900">{actual}</td>
                        <td className="p-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            status === 'Returned' ? 'bg-green-100 text-green-800' :
                            status === 'Active' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-900">{teacher?.name || 'Unknown'}</td>
                        <td className="p-4 text-sm text-gray-500">{pass.notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {studentHistory.length === 0 && (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No hall pass history found for this student.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}